import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import { KnowledgeAskCitation, SocialCitationPost } from '@digital-twins/shared-types'
import { cn } from '@/lib/utils'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { formatSocialDate, SOCIAL_PLATFORM_LABELS } from './socialFormat'

/** The cited post when a citation points at one social post rather than a page. */
export function citationSocialPost(citation: KnowledgeAskCitation): SocialCitationPost | null {
  if (citation.granularity !== 'post' || !citation.post) {
    return null
  }
  return citation.post
}

/**
 * The cited passage when it is a literal part of the post text. The provider
 * may quote the whole digest record, whose machine header is not the post's.
 */
export function socialCitationQuote(postText: string | null | undefined, snippet: string | null | undefined): string | null {
  const text = postText?.trim() ?? ''
  const quote = snippet?.trim() ?? ''
  if (quote === '' || quote === text || !text.includes(quote)) {
    return null
  }
  return quote
}

/** The words of the post this answer leaned on: the cited passage, else the post text. */
export function socialCitationExcerpt(citation: KnowledgeAskCitation): string {
  return socialCitationQuote(citation.post?.text, citation.snippet) ?? citation.post?.text?.trim() ?? ''
}

interface SocialCitationPreviewProps {
  post: SocialCitationPost
  excerpt: string
  testId?: string
}

/** Thumbnail, author and excerpt of a cited post, for tooltips and source lists. */
export function SocialCitationPreview(props: SocialCitationPreviewProps) {
  const { post, excerpt, testId } = props
  const { t } = useTranslation('app')
  const [hasImageFailed, setHasImageFailed] = useState(false)
  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')

  return (
    <span className="flex min-w-0 items-start gap-2 text-left" data-testid={testId}>
      {post.thumbnailUrl && !hasImageFailed ? (
        <img
          src={post.thumbnailUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setHasImageFailed(true)}
          className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
        />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
          <SocialPlatformIcon platform={post.platform} className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 space-y-0.5">
        <span className="block truncate text-xs font-medium">
          {author} · {SOCIAL_PLATFORM_LABELS[post.platform]}
        </span>
        {excerpt ? (
          <span className="line-clamp-3 block text-xs text-muted-foreground">{excerpt}</span>
        ) : null}
      </span>
    </span>
  )
}

/** Short marker label for a cited post, e.g. "Reddit @jane". */
export function socialCitationLabel(post: SocialCitationPost): string {
  const platform = SOCIAL_PLATFORM_LABELS[post.platform]
  return post.authorHandle ? `${platform} @${post.authorHandle}` : platform
}

interface SocialCitationPostMetaProps {
  post: SocialCitationPost
  className?: string
  testId?: string
}

/** Platform · @author · date · tracker, with a link out to the original post. */
export function SocialCitationPostMeta(props: SocialCitationPostMetaProps) {
  const { post, className, testId = 'social-citation-post' } = props

  const { t, i18n } = useTranslation('app')

  const date = formatSocialDate(post.publishedAt, i18n.language)
  const parts = [
    SOCIAL_PLATFORM_LABELS[post.platform],
    post.authorHandle ? `@${post.authorHandle}` : t('socialListening.detail.unknownAuthor'),
    date,
    post.trackerName,
  ].filter(Boolean)

  return (
    <span
      className={cn(
        'flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground',
        className,
      )}
      data-testid={testId}
    >
      <SocialPlatformIcon platform={post.platform} className="h-3 w-3" />
      <span>{parts.join(' · ')}</span>
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-0.5 text-foreground underline-offset-2 hover:underline"
        data-testid={`${testId}-link`}
      >
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
        {t('socialListening.citation.openPost')}
      </a>
    </span>
  )
}
